import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import { formatDateLong } from "@/utils";
import { ArrowLeft, ArrowRight } from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { SortOption } from "@/types/component";
import type { ClientCertificate } from "../types/client-certificates.types";
import { clientCertificatesApi } from "../services/client-certificates.api";
import CertificateCard from "../components/CertificateCard";
import { EnrollmentCertificateViewModal } from "@/features/client-exams";
import { clientExamsApi } from "@/features/client-exams/services/client-exams.api";
import { generateAndDownloadCertificatePDFFromHtml } from "@/features/certificates";
import styles from "./ClientCertificatesPage.module.scss";

const PAGE_SIZE_OPTIONS = [12, 24, 48];

function parseSortValue(
  value: string
): { sortBy: "issuedAt" | "educationName"; sortOrder: "asc" | "desc" } {
  switch (value) {
    case "issuedAsc":
      return { sortBy: "issuedAt", sortOrder: "asc" };
    case "educationAsc":
      return { sortBy: "educationName", sortOrder: "asc" };
    case "educationDesc":
      return { sortBy: "educationName", sortOrder: "desc" };
    case "issuedDesc":
    default:
      return { sortBy: "issuedAt", sortOrder: "desc" };
  }
}

const ClientCertificatesPage: React.FC = () => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortValue, setSortValue] = useState("issuedDesc");
  const [certificates, setCertificates] = useState<ClientCertificate[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewCertificate, setViewCertificate] = useState<ClientCertificate | null>(null);
  // Staj sertifikası eğitim sertifikalarından ayrı bir kaynaktan gelir; stajyer
  // kendi belgesini buradan indirir (önceden yalnız yönetim ekranından indiriliyordu).
  const [internCertificate, setInternCertificate] = useState<{
    id: string;
    isCompleted: boolean;
    startDate?: string | null;
    endDate?: string | null;
  } | null>(null);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const sortOptions: SortOption[] = [
    {
      label: t("clientCertificates.sort.issuedDesc"),
      value: "issuedDesc",
    },
    {
      label: t("clientCertificates.sort.issuedAsc"),
      value: "issuedAsc",
    },
    {
      label: t("clientCertificates.sort.educationAsc"),
      value: "educationAsc",
    },
    {
      label: t("clientCertificates.sort.educationDesc"),
      value: "educationDesc",
    },
  ];

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedItems = useMemo(
    () => certificates,
    [certificates],
  );

  useEffect(() => {
    const { sortBy, sortOrder } = parseSortValue(sortValue);

    setLoading(true);
    clientCertificatesApi
      .getMyCertificates({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        sortBy,
        sortOrder,
      })
      .then(({ items, count }) => {
        setCertificates(items);
        setTotalRecords(count);
      })
      .catch(() => {
        setCertificates([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [currentPage, pageSize, debouncedSearch, sortValue]);
  useEffect(() => {
    clientCertificatesApi
      .getMyInternCertificateInfo()
      .then(setInternCertificate)
      .catch(() => setInternCertificate(null));
  }, []);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };
  const handleSortChange = (option: SortOption) => {
    setSortValue(option.value);
    setCurrentPage(1);
  };

  const handleCertificateAction = async (
    certificate: ClientCertificate,
    action: string
  ) => {
    if (action === "view") {
      setViewCertificate(certificate);
    } else if (action === "download") {
      if (!certificate.enrollmentId) return;
      const filename = (certificate.title || "sertifika").replace(/[^\w\s-]/gi, "").replace(/\s+/g, "-") || "sertifika";
      try {
        let html: string | null = null;
        if (certificate.educationId && certificate.enrollmentId) {
          const data = await clientExamsApi.getEnrollmentCertificate(
            certificate.educationId,
            certificate.enrollmentId
          );
          html = data.parsedHtml ?? null;
        }
        if (!html) {
          html = await clientCertificatesApi.getCertificateHtml(certificate.enrollmentId);
        }
        if (html) {
          await generateAndDownloadCertificatePDFFromHtml(html, filename);
        } else {
          clientCertificatesApi.downloadCertificatePdf(certificate.enrollmentId);
        }
      } catch {
        clientCertificatesApi.downloadCertificatePdf(certificate.enrollmentId);
      }
    }
  };

  return (
    <DashboardLayout>
      <Header title={t("clientCertificates.title")} />

      <EnrollmentCertificateViewModal
        educationId={viewCertificate?.educationId}
        enrollmentId={viewCertificate?.enrollmentId}
        titleOverride={viewCertificate?.title}
        isOpen={Boolean(viewCertificate)}
        onClose={() => setViewCertificate(null)}
        onDownload={(enrollmentId) => {
          clientCertificatesApi.downloadCertificatePdf(enrollmentId);
        }}
      />

      <div className={styles.pageContainer}>
        <PageHeader
          searchPlaceholder={t("clientCertificates.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          showSort
          sortOptions={sortOptions}
          selectedSortValue={sortValue}
          onSort={handleSortChange}
        />

        {/* Staj sertifikası bölümü yalnız stajyer profili olan kullanıcıya
            çıkar. Staj sürerken de görünür — belge henüz düzenlenmediği için
            indirme yerine bilgilendirme gösterilir; eskiden bu durumda sayfada
            staj tarafına dair hiçbir iz olmuyordu. */}
        {internCertificate && (
          <div className={styles.internCertificateBanner}>
            <div className={styles.internCertificateText}>
              <span className={styles.internCertificateTitle}>
                {t("clientCertificates.internCertificateTitle")}
              </span>
              {internCertificate.startDate && internCertificate.endDate && (
                <span className={styles.internCertificateMeta}>
                  {formatDateLong(internCertificate.startDate)} –{" "}
                  {formatDateLong(internCertificate.endDate)}
                </span>
              )}
              {!internCertificate.isCompleted && (
                <span className={styles.internCertificateMeta}>
                  {t("clientCertificates.internCertificatePending")}
                </span>
              )}
            </div>
            {internCertificate.isCompleted && (
              <Button
                color="primary"
                onClick={() => clientCertificatesApi.downloadMyInternCertificatePdf()}
              >
                {t("clientCertificates.internCertificateDownload")}
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className={styles.emptyState}>
            {t("clientCertificates.loading")}
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className={styles.emptyState}>
            {t("clientCertificates.noData")}
          </div>
        ) : (
          <>
            <div className={styles.certificateGrid}>
              {paginatedItems.map((cert) => (
                <CertificateCard
                  key={cert.id}
                  certificate={cert}
                  onActionClick={handleCertificateAction}
                />
              ))}
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <Dropdown
                  onSelect={(v) =>
                    handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                  }
                  options={PAGE_SIZE_OPTIONS.map((size) => ({
                    label: size.toString(),
                    value: size.toString(),
                  }))}
                  menuPosition="top"
                  selectedLabel={pageSize.toString()}
                  value={pageSize.toString()}
                />
                <span className={styles.paginationDetails}>
                  {t("pagination.details", {
                    total: totalRecords,
                    start: (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  handlePageChange
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight height={16} width={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClientCertificatesPage;
